import React, { useState } from 'react';
import { MapPin, Search, CheckCircle, Navigation, ArrowRight } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import axios from 'axios';
import { getAuthHeader } from '../../services/otpAuthService';

interface Studio {
  _id: string;
  ragioneSociale: string;
  citta: string;
  provincia: string;
  indirizzo: string;
  civico: string;
  telefono?: string;
  email: string;
  coordinates?: {
    lat: number;
    lng: number;
  };
  parentMemberId?: string;
  isSecondarySede?: boolean;
  distanza?: number;
}

interface StepRicercaMappaProps {
  onComplete: (studiSelezionati: Studio[]) => void;
  initialSelection: Studio[];
}

const StepRicercaMappa: React.FC<StepRicercaMappaProps> = ({ onComplete, initialSelection }) => {
  const [indirizzoVenditore, setIndirizzoVenditore] = useState('');
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [studiDisponibili, setStudiDisponibili] = useState<Studio[]>([]);
  const [studiSelezionati, setStudiSelezionati] = useState<Studio[]>(initialSelection);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [luogoRisolto, setLuogoRisolto] = useState('');

  // URL base per API REST (senza /graphql)
  const API_BASE_URL = process.env.REACT_APP_API_URL
    ? process.env.REACT_APP_API_URL.replace('/graphql', '')
    : 'http://localhost:5000';

  const handleCercaStudi = async () => {
    if (!indirizzoVenditore.trim()) {
      toast.error('Inserisci l\'indirizzo del venditore');
      return;
    }

    setLoading(true);

    try {
      // Geocodifica l'indirizzo del venditore con addressdetails per risultati migliori
      console.log('🔍 Geocoding indirizzo:', indirizzoVenditore);
      let geocodeResponse = await axios.get(
        `https://nominatim.openstreetmap.org/search`, {
          params: {
            format: 'json',
            q: indirizzoVenditore,
            countrycodes: 'it',
            limit: 5,
            addressdetails: 1
          },
          headers: { 'User-Agent': 'UNASCA-SvincoloAtti/1.0' }
        }
      );

      console.log('📍 Geocode response:', geocodeResponse.data);

      if (!geocodeResponse.data || geocodeResponse.data.length === 0) {
        toast.error('Indirizzo non trovato. Prova a essere più specifico.');
        setLoading(false);
        return;
      }

      // Seleziona il miglior risultato: preferisci risultati di tipo city/town/village/road
      // rispetto a county/state (che corrispondono a province/regioni)
      let bestResult = geocodeResponse.data[0];
      const preferredTypes = ['building', 'house', 'road', 'residential', 'village', 'town', 'city', 'suburb', 'neighbourhood'];
      for (const result of geocodeResponse.data) {
        if (preferredTypes.includes(result.addresstype) || preferredTypes.includes(result.type)) {
          bestResult = result;
          break;
        }
      }

      const venditoreLat = parseFloat(bestResult.lat);
      const venditoreLng = parseFloat(bestResult.lon);
      const displayName = bestResult.display_name;

      // Mostra all'utente il luogo risolto
      setLuogoRisolto(displayName);
      console.log('📍 Coordinate venditore:', { lat: venditoreLat, lng: venditoreLng, displayName });

      // Carica gli studi della rete certificata
      // (l'endpoint restituisce per default solo certificati ATTIVI)
      console.log('🔍 Caricamento studi...');
      const studiResponse = await axios.get(
        `${API_BASE_URL}/api/public/certified-network/map`,
        { headers: getAuthHeader() }
      );

      // Il backend restituisce {success: true, data: {members: [...], total: ...}}
      const responseData = studiResponse.data.data;

      if (!responseData || !responseData.members) {
        console.error('❌ Dati non validi:', studiResponse.data);
        toast.error('Errore nel caricamento degli studi');
        setLoading(false);
        return;
      }

      // Calcola distanze e ordina
      const studiConDistanza = responseData.members
        .filter((studio: any) => studio.coordinates && studio.coordinates.lat && studio.coordinates.lng)
        .map((studio: any) => {
          const distanza = calcolaDistanza(
            venditoreLat,
            venditoreLng,
            studio.coordinates.lat,
            studio.coordinates.lng
          );

          // Il backend restituisce campi strutturati (citta, indirizzo, civico)
          return {
            _id: studio.id,
            ragioneSociale: studio.businessName,
            citta: studio.citta || '',
            provincia: studio.province,
            indirizzo: studio.indirizzo || '',
            civico: studio.civico || '',
            telefono: studio.phone,
            email: studio.email,
            coordinates: studio.coordinates,
            parentMemberId: studio.parentMemberId,
            isSecondarySede: studio.isSecondarySede,
            distanza
          };
        })
        .sort((a: Studio, b: Studio) => (a.distanza || 0) - (b.distanza || 0));

      setStudiDisponibili(studiConDistanza);
      setMapLoaded(true);
      toast.success(`Trovati ${studiConDistanza.length} studi`);

    } catch (error: any) {
      console.error('Errore ricerca studi:', error);
      toast.error('Errore durante la ricerca degli studi');
    } finally {
      setLoading(false);
    }
  };

  // Calcola distanza in km tra due coordinate (formula di Haversine)
  const calcolaDistanza = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371; // Raggio della Terra in km
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const toRad = (value: number): number => {
    return (value * Math.PI) / 180;
  };

  // Identita dello studio dietro un marker: la sede secondaria condivide
  // il parentMemberId con la sede principale dello stesso studio.
  const studioKey = (s: Studio) => s.parentMemberId || s._id;

  const toggleStudioSelection = (studio: Studio) => {
    const isSelected = studiSelezionati.some(s => s._id === studio._id);

    if (isSelected) {
      setStudiSelezionati(studiSelezionati.filter(s => s._id !== studio._id));
      return;
    }

    // Impedisci lo stesso studio in piu sedi
    const giaSelezionatoAltraSede = studiSelezionati.find(
      s => studioKey(s) === studioKey(studio) && s._id !== studio._id
    );
    if (giaSelezionatoAltraSede) {
      toast.error(
        `Hai già selezionato «${studio.ragioneSociale.split(' - ')[0]}» in un'altra sede. Puoi sceglierne una sola.`
      );
      return;
    }

    setStudiSelezionati([...studiSelezionati, studio]);
  };

  // Marker disabilitato: stesso studio gia selezionato in un'altra sede
  const isStudioDisabilitato = (studio: Studio) =>
    studiSelezionati.some(s => studioKey(s) === studioKey(studio) && s._id !== studio._id);

  const handleContinua = () => {
    if (studiSelezionati.length === 0) {
      toast.error('Seleziona almeno uno studio');
      return;
    }

    onComplete(studiSelezionati);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Cerca Studio sulla Mappa
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Inserisci l'indirizzo del venditore per trovare i colleghi più vicini
        </p>
      </div>

      {/* Form Ricerca */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          <MapPin className="w-4 h-4 inline mr-2" />
          Indirizzo del Venditore
        </label>
        <div className="flex gap-3">
          <input
            type="text"
            value={indirizzoVenditore}
            onChange={(e) => setIndirizzoVenditore(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleCercaStudi()}
            placeholder="Es: Via Roma 10, Milano"
            className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          />
          <button
            onClick={handleCercaStudi}
            disabled={loading}
            className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Ricerca...
              </>
            ) : (
              <>
                <Search className="w-5 h-5" />
                Cerca
              </>
            )}
          </button>
        </div>
      </div>

      {/* Feedback luogo risolto */}
      {luogoRisolto && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
          <p className="text-sm text-blue-800 dark:text-blue-300">
            <MapPin className="w-4 h-4 inline mr-1" />
            <strong>Posizione trovata:</strong> {luogoRisolto}
          </p>
          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
            Se non corrisponde al luogo desiderato, prova ad aggiungere più dettagli (es: "Via Roma 10, Chieti, CH")
          </p>
        </div>
      )}

      {/* Lista Studi */}
      {mapLoaded && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Studi Disponibili ({studiDisponibili.length})
            </h3>
            {studiSelezionati.length > 0 && (
              <span className="text-sm text-purple-600 dark:text-purple-400 font-medium">
                {studiSelezionati.length} selezionati
              </span>
            )}
          </div>

          <div className="space-y-3 max-h-96 overflow-y-auto">
            {studiDisponibili.map((studio) => {
              const isSelected = studiSelezionati.some(s => s._id === studio._id);
              const disabilitato = !isSelected && isStudioDisabilitato(studio);

              return (
                <div
                  key={studio._id}
                  onClick={() => !disabilitato && toggleStudioSelection(studio)}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    disabilitato
                      ? 'border-gray-200 dark:border-gray-700 opacity-50 cursor-not-allowed'
                      : 'cursor-pointer ' + (isSelected
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-700')
                  }`}
                  title={disabilitato ? 'Studio già selezionato in un\'altra sede' : undefined}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-base font-semibold text-gray-900 dark:text-white">
                          {studio.ragioneSociale}
                        </h4>
                        {isSelected && (
                          <CheckCircle className="w-5 h-5 text-purple-600" />
                        )}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {studio.indirizzo} {studio.civico}, {studio.citta} ({studio.provincia})
                      </p>
                      {studio.telefono && (
                        <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                          📞 {studio.telefono}
                        </p>
                      )}
                    </div>
                    <div className="text-right ml-4">
                      <div className="flex items-center gap-1 text-purple-600 dark:text-purple-400 font-semibold">
                        <Navigation className="w-4 h-4" />
                        {studio.distanza?.toFixed(1)} km
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Bottone Continua */}
      {studiSelezionati.length > 0 && (
        <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={handleContinua}
            className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
          >
            Continua
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  );
};

export default StepRicercaMappa;

